import { ref, watch } from 'vue';
import type { Ref } from 'vue';

type Theme = 'light' | 'dark';

const getSystemTheme = (): Theme => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

const theme: Ref<Theme> = ref((localStorage.getItem('theme') as Theme) || getSystemTheme());

interface UseThemeReturn {
  theme: Ref<Theme>
  toggleTheme: () => void
  setTheme: (newTheme: Theme) => void
}

export function useTheme(): UseThemeReturn {
  const toggleTheme = (): void => {
    theme.value = theme.value === 'light' ? 'dark' : 'light';
  };

  const setTheme = (newTheme: Theme): void => {
    theme.value = newTheme;
  };

  watch(theme, (newTheme: Theme) => {
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  }, { immediate: true });

  // Listen for system theme changes
  if (typeof window !== 'undefined' && window.matchMedia) {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', (e: MediaQueryListEvent) => {
      if (!localStorage.getItem('theme')) {
        theme.value = e.matches ? 'dark' : 'light';
      }
    });
  }

  return {
    theme,
    toggleTheme,
    setTheme
  };
}
